import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import type { Responsible } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/lib/notifications';
import { useResponsibles } from '@/features/responsibles/useResponsibles';
import {
  deleteResponsibleCallable,
  setResponsibleActiveCallable,
  setResponsibleDefaultCallable,
} from '@/lib/callables';
import { ResponsibleAddDialog } from '@/features/responsibles/ResponsibleAddDialog';

type Props = {
  tenantId: string;
};

type RowAction = 'setDefault' | 'toggleActive' | 'delete';

/**
 * Aba de responsáveis do tenant (admin, Cloudscape). Lista e permite adicionar
 * / ativar-desativar / definir padrão / excluir via callables estendidos para
 * super-admin. Não há renomear (sem callable). Exclusão de responsável em uso
 * é bloqueada pelo servidor.
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200.
 *
 * Contrato: `setResponsibleDefault`, `setResponsibleActive` e
 * `deleteResponsible` (com confirmação), todos com `{ tenantId, responsibleId }`;
 * a lista reflete pelo listener (`useResponsibles`).
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Responsáveis).
 *
 * Dados: `tenants/{tenantId}/responsibles` (`useResponsibles`).
 *
 * Falhas conhecidas:
 *  - padrão e exclusão: o tratamento de erro procura `'failed-precondition'` dentro de
 *    `err.message`, mas o `FirebaseError` traz ali o texto do servidor
 *    ("Responsável já usado em etiqueta — desative em vez de excluir.") e o código em `err.code`. A mensagem específica nunca
 *    aparece (ver #926, achados).
 *  - 2026-10-25 (#1021): o erro da exclusão ia só por toast, atrás do overlay
 *    do confirm (que fica aberto no erro). Hoje é `Alert` dentro do confirm.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro da exclusão em `Alert` no confirm
 *    (mesmo achado do CRM, #174). Padrão/ativar seguem em toast (sem modal).
 */
export function ResponsiblesTab({ tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const result = useResponsibles(tenantId);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Responsible | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // #1021 (2026-10-25 15:00 +0100): erro da exclusão, mostrado dentro do confirm.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleSetDefault(r: Responsible): Promise<void> {
    setBusyId(r.id);
    try {
      await setResponsibleDefaultCallable({ tenantId, responsibleId: r.id });
      toast.success(t('adminResponsibles.toast.defaultSet'));
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      toast.error(
        code.includes('failed-precondition')
          ? t('adminResponsibles.errors.activateFirst')
          : t('adminResponsibles.errors.generic'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggleActive(r: Responsible): Promise<void> {
    setBusyId(r.id);
    try {
      await setResponsibleActiveCallable({ tenantId, responsibleId: r.id, active: !r.active });
      toast.success(r.active ? t('adminResponsibles.toast.deactivated') : t('adminResponsibles.toast.activated'));
    } catch {
      toast.error(t('adminResponsibles.errors.generic'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    setDeleteError(null);
    try {
      await deleteResponsibleCallable({ tenantId, responsibleId: deleteTarget.id });
      toast.success(t('adminResponsibles.toast.deleted'));
      setDeleteTarget(null);
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setDeleteError(
        code.includes('failed-precondition')
          ? t('adminResponsibles.errors.inUse')
          : t('adminResponsibles.errors.generic'),
      );
    } finally {
      setBusyId(null);
    }
  }

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('adminResponsibles.errorLoad')}</Alert>;
  }

  const responsibles = result.responsibles;

  const columns: TableProps.ColumnDefinition<Responsible>[] = [
    {
      id: 'name',
      header: t('adminResponsibles.columns.name'),
      cell: (r): React.ReactNode => <Box fontWeight="bold">{r.name}</Box>,
      isRowHeader: true,
    },
    {
      id: 'status',
      header: t('adminResponsibles.columns.status'),
      cell: (r): React.ReactNode => (
        <SpaceBetween direction="horizontal" size="xs">
          {r.isDefault && <Badge color="green">{t('adminResponsibles.badge.default')}</Badge>}
          {r.active ? (
            !r.isDefault && <Badge color="grey">{t('adminResponsibles.badge.active')}</Badge>
          ) : (
            <Badge color="red">{t('adminResponsibles.badge.inactive')}</Badge>
          )}
        </SpaceBetween>
      ),
    },
    {
      id: 'usageCount',
      header: t('adminResponsibles.columns.usageCount'),
      cell: (r): React.ReactNode => r.usageCount,
    },
    {
      id: 'createdAt',
      header: t('adminResponsibles.columns.createdAt'),
      cell: (r): React.ReactNode => formatDateTime(r.createdAtMillis, locale),
    },
    {
      id: 'actions',
      header: t('adminResponsibles.columns.actions'),
      cell: (r): React.ReactNode => (
        <ButtonDropdown
          expandToViewport
          loading={busyId === r.id}
          disabled={busyId !== null}
          items={[
            { id: 'setDefault', text: t('adminResponsibles.actions.setDefault'), disabled: r.isDefault || !r.active },
            {
              id: 'toggleActive',
              text: r.active
                ? t('adminResponsibles.actions.deactivate')
                : t('adminResponsibles.actions.activate'),
            },
            { id: 'delete', text: t('adminResponsibles.actions.delete') },
          ]}
          onItemClick={({ detail }): void => {
            const action = detail.id as RowAction;
            if (action === 'setDefault') void handleSetDefault(r);
            else if (action === 'toggleActive') void handleToggleActive(r);
            else {
              setDeleteError(null);
              setDeleteTarget(r);
            }
          }}
        >
          {t('adminResponsibles.columns.actions')}
        </ButtonDropdown>
      ),
    },
  ];

  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        description={t('adminResponsibles.description')}
        actions={
          <Button
            variant="primary"
            onClick={(): void => {
              setAddOpen(true);
            }}
          >
            {t('adminResponsibles.actions.add')}
          </Button>
        }
      >
        {t('adminResponsibles.title')}
      </Header>

      <Table<Responsible>
        items={responsibles}
        trackBy="id"
        variant="container"
        columnDefinitions={columns}
        empty={
          <Box textAlign="center" color="inherit">
            {t('adminResponsibles.empty')}
          </Box>
        }
      />

      <ResponsibleAddDialog
        open={addOpen}
        tenantId={tenantId}
        onClose={(): void => {
          setAddOpen(false);
        }}
      />
      <AdminConfirm
        visible={deleteTarget !== null}
        title={t('adminResponsibles.deleteDialog.title')}
        confirmLabel={t('adminResponsibles.deleteDialog.confirm')}
        cancelLabel={t('adminResponsibles.addDialog.cancel')}
        loading={busyId !== null && deleteTarget !== null}
        onConfirm={(): void => {
          void handleDelete();
        }}
        onDismiss={(): void => {
          if (busyId === null) setDeleteTarget(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {t('adminResponsibles.deleteDialog.body', { name: deleteTarget?.name ?? '' })}
          </Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {deleteError !== null && <Alert type="error">{deleteError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </SpaceBetween>
  );
}
